import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { Plus, ChevronRight, Zap, Film, Clock } from "lucide-react";
import TopNav from "../components/TopNav";
import { LineChart, Line, ResponsiveContainer } from "recharts";
import { DemoBadge } from "../components/DemoBadge";

const API = process.env.REACT_APP_BACKEND_URL + "/api";


const CE_NAMES = {
  "CE-01": "Romance", "CE-02": "Joy", "CE-03": "Sorrow",
  "CE-04": "Fury", "CE-05": "Valor", "CE-06": "Dread",
  "CE-07": "Revulsion", "CE-08": "Wonder", "CE-09": "Serenity",
};

const GENRES = ["Romance", "Thriller", "Revenge", "Family Drama", "Fantasy", "Comedy", "Crime", "Horror", "Sci-Fi", "Custom"];
const PLATFORMS = ["reelshort", "dramabox", "flextv", "goodshort", "general"];

const STATUS_COLORS = { draft: "var(--ink-tertiary)", in_progress: "var(--accent-gold)", completed: "var(--semantic-success)", archived: "var(--ink-tertiary)" };

function NewSeriesModal({ onClose, onCreate }) {
  const [form, setForm] = useState({ title: "", genre: "Romance", platform_target: "general", total_episodes: 60, description: "" });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/projects`, form, { withCredentials: true });
      onCreate(data);
      onClose();
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        className="relative w-full max-w-md rounded-2xl p-6 z-10"
        style={{ background: "var(--surface-raised)", border: "1px solid var(--border-subtle)" }}
      >
        <h3 className="font-bold text-lg mb-5">New Series</h3>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="text-[var(--ink-secondary)] text-sm block mb-1.5">Series Title *</label>
            <input
              value={form.title}
              onChange={e => setForm({ ...form, title: e.target.value })}
              required
              data-testid="new-series-title"
              placeholder="e.g. Love in the C-Suite"
              className="w-full px-4 py-2.5 rounded-xl text-sm outline-none"
              style={{ background: "var(--surface-card)", border: "1px solid var(--border-subtle)" }}
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[var(--ink-secondary)] text-sm block mb-1.5">Genre</label>
              <select
                value={form.genre}
                onChange={e => setForm({ ...form, genre: e.target.value })}
                data-testid="new-series-genre"
                className="w-full px-3 py-2.5 rounded-xl text-sm outline-none"
                style={{ background: "var(--surface-card)", border: "1px solid var(--border-subtle)" }}
              >
                {GENRES.map(g => <option key={g} value={g}>{g}</option>)}
              </select>
            </div>
            <div>
              <label className="text-[var(--ink-secondary)] text-sm block mb-1.5">Platform</label>
              <select
                value={form.platform_target}
                onChange={e => setForm({ ...form, platform_target: e.target.value })}
                data-testid="new-series-platform"
                className="w-full px-3 py-2.5 rounded-xl text-sm outline-none capitalize"
                style={{ background: "var(--surface-card)", border: "1px solid var(--border-subtle)" }}
              >
                {PLATFORMS.map(p => <option key={p} value={p} className="capitalize">{p.charAt(0).toUpperCase() + p.slice(1)}</option>)}
              </select>
            </div>
          </div>
          <div>
            <label className="text-[var(--ink-secondary)] text-sm block mb-1.5">Total Episodes</label>
            <input
              type="number"
              value={form.total_episodes}
              onChange={e => setForm({ ...form, total_episodes: parseInt(e.target.value) || 60 })}
              min={1} max={500}
              data-testid="new-series-episodes"
              className="w-full px-4 py-2.5 rounded-xl text-sm outline-none"
              style={{ background: "var(--surface-card)", border: "1px solid var(--border-subtle)" }}
            />
          </div>
          <div>
            <label className="text-[var(--ink-secondary)] text-sm block mb-1.5">Description (optional)</label>
            <textarea
              value={form.description}
              onChange={e => setForm({ ...form, description: e.target.value })}
              rows={2}
              placeholder="Brief premise of your series..."
              className="w-full px-4 py-2.5 rounded-xl text-sm outline-none resize-none"
              style={{ background: "var(--surface-card)", border: "1px solid var(--border-subtle)" }}
            />
          </div>
          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose}
              className="flex-1 py-2.5 rounded-xl text-[var(--ink-secondary)] text-sm border transition-colors"
              style={{ borderColor: "var(--border-subtle)" }}>
              Cancel
            </button>
            <button type="submit" disabled={loading} data-testid="create-series-button"
              className="flex-1 py-2.5 rounded-xl font-semibold text-sm transition-all"
              style={{ background: "var(--accent-gold)" }}>
              {loading ? "Creating..." : "Create Series"}
            </button>
          </div>
        </form>
      </motion.div>
    </div>
  );
}

function SeriesCard({ project, index, onClick }) {
  const sparkData = project.arc_data?.map(d => ({ v: d.intensity })) || [];
  const ceColor = "var(--accent-gold)"; // Replaced token logic applied directly in UI if needed
  const statusColor = STATUS_COLORS[project.status] || "var(--ink-tertiary)";

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.05 }}
      onClick={onClick}
      data-testid={`series-card-${project.id}`}
      className="arc-card cursor-pointer rounded-xl p-5 group"
      style={{ background: "var(--surface-card)", border: "1px solid var(--border-subtle)" }}
    >
      <div className="flex items-start justify-between mb-3">
        <div className="flex-1 min-w-0 pr-3">
          <div className="flex items-center gap-2">
            <h3 className="font-bold text-base truncate group-hover:text-[var(--accent-gold)] transition-colors">
              {project.title}
            </h3>
            {project.is_demo && <DemoBadge variant="compact" />}
          </div>
          <div className="flex items-center gap-2 mt-1 flex-wrap">
            <span className="text-xs px-2 py-0.5 rounded-full font-medium" style={{ background: "var(--accent-gold)20", color: "var(--accent-gold)", border: "1px solid var(--accent-gold)40" }}>
              {project.genre}
            </span>
            <span className="text-xs px-2 py-0.5 rounded-full font-medium capitalize" style={{ background: "var(--surface-card)", color: "var(--ink-tertiary)", border: "1px solid var(--border-subtle)" }}>
              {project.platform_target}
            </span>
          </div>
        </div>
        <div className="text-xs px-2 py-1 rounded-full capitalize font-medium"
          style={{ background: `${statusColor}20`, color: statusColor, border: `1px solid ${statusColor}40` }}>
          {project.status?.replace("_", " ")}
        </div>
      </div>

      {/* Sparkline */}
      <div className="h-12 my-3">
        {sparkData.length > 1 ? (
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={sparkData}>
              <Line type="monotone" dataKey="v" stroke={ceColor} strokeWidth={2} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        ) : (
          <div className="h-full flex items-center justify-center">
            <span className="text-[var(--ink-tertiary)] text-xs">No arc data yet</span>
          </div>
        )}
      </div>

      <div className="flex items-center justify-between text-xs text-[var(--ink-tertiary)] mt-2">
        <div className="flex items-center gap-1">
          <Film size={11} />
          <span>{project.episode_count || 0} / {project.total_episodes} episodes</span>
        </div>
        <ChevronRight size={14} className="text-[var(--border-subtle)] group-hover:text-[var(--accent-gold)] transition-colors" />
      </div>
    </motion.div>
  );
}

export default function StudioPage() {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const navigate = useNavigate();

  const load = async () => {
    try {
      const { data } = await axios.get(`${API}/projects`, { withCredentials: true });
      setProjects(data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  return (
    <div className="min-h-screen" style={{ background: "var(--surface-page)" }}>
      <TopNav />
      <div className="max-w-7xl mx-auto px-6 py-8">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-black tracking-tight">Your Studio</h1>
            <p className="text-[var(--ink-secondary)] text-sm mt-1">Your series at a glance.</p>
          </div>
          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={() => setShowModal(true)}
            data-testid="new-series-button"
            className="flex items-center gap-2 px-5 py-2.5 rounded-xl font-semibold text-sm"
            style={{ background: "var(--accent-gold)" }}
          >
            <Plus size={16} />
            New Series
          </motion.button>
        </div>

        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="h-48 rounded-xl animate-pulse" style={{ background: "var(--surface-card)" }} />
            ))}
          </div>
        ) : projects.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-24">
            <motion.div
              animate={{ y: [0, -8, 0] }}
              transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
              className="w-24 h-24 rounded-2xl flex items-center justify-center mb-6"
              style={{ background: "var(--surface-card)", border: "2px dashed var(--border-subtle)" }}
            >
              <Film size={40} className="text-[var(--border-subtle)]" />
            </motion.div>
            <h3 className="font-bold text-xl mb-2">Your Studio is empty</h3>
            <p className="text-[var(--ink-tertiary)] text-sm mb-6">Create your first series to start writing emotionally powerful scripts.</p>
            <button
              onClick={() => setShowModal(true)}
              data-testid="empty-new-series-button"
              className="flex items-center gap-2 px-6 py-3 rounded-xl font-semibold"
              style={{ background: "var(--accent-gold)" }}
            >
              <Plus size={16} />
              Create your first series
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {projects.map((p, i) => (
              <SeriesCard
                key={p.id}
                project={p}
                index={i}
                onClick={() => navigate(`/studio/${p.id}`)}
              />
            ))}
          </div>
        )}
      </div>

      {showModal && (
        <NewSeriesModal
          onClose={() => setShowModal(false)}
          onCreate={(p) => { setProjects([p, ...projects]); }}
        />
      )}
    </div>
  );
}
